import React, { useState, useEffect } from 'react';
import { FiSave, FiEdit3, FiType } from 'react-icons/fi';

/**
 * Componente para editar texto customizado
 */
export default function TextEditor({ 
  currentText, 
  onTextSave, 
  placeholder = "Digite o texto...",
  label = "Texto Customizado",
  maxLength = 200,
  cardTitle = "",
  cardHint = ""
}) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(currentText || '');
  }, [currentText]);

  async function handleSave() {
    if (!text.trim()) return;
    
    setSaving(true);
    try {
      await onTextSave(text.trim());
      setEditing(false);
    } catch (err) {
      console.error('Erro ao salvar texto:', err);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setText(currentText || '');
    setEditing(false);
  }

  return (
    <div style={styles.container}>
      {/* Header do Card */}
      {cardTitle && (
        <div style={styles.cardHeader}>
          <div style={styles.cardHeaderLeft}>
            <div style={styles.cardTitle}>{cardTitle}</div>
            <div style={styles.cardHint}>{cardHint}</div>
          </div>
          <div style={styles.cardHeaderRight}>
            {!editing && (
              <button onClick={() => setEditing(true)} style={styles.editButtonCard}>
                <FiEdit3 size={14} /> Editar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header interno (label) */}
      <div style={styles.header}>
        <FiType size={14} style={{marginRight: 6}} /> {label}
      </div>

      {editing ? (
        <div style={styles.editMode}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            style={styles.textarea}
            rows={4}
          />
          <div style={styles.charCount}>
            {text.length}/{maxLength} caracteres
          </div>
          <div style={styles.actions}>
            <button onClick={handleCancel} style={styles.cancelButton}>
              Cancelar
            </button>
            <button 
              onClick={handleSave} 
              style={styles.saveButton}
              disabled={!text.trim() || saving}
            >
              <FiSave size={14} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.preview}>
          {currentText ? (
            <div style={styles.currentText}>{currentText}</div>
          ) : (
            <div style={styles.emptyText}>
              Nenhum texto customizado definido. O app usará o texto padrão.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: '2px solid #e8e9ef',
  },
  cardHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#333',
  },
  cardHint: {
    fontSize: 12,
    color: '#666',
  },
  cardHeaderRight: {
    display: 'flex',
    alignItems: 'center',
  },
  editButtonCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    userSelect: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    paddingBottom: 8,
    marginBottom: 8,
    borderBottom: '1px solid #e8e9ef',
  },
  editMode: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  textarea: {
    width: '100%',
    padding: 12,
    border: '2px solid #e8e9ef',
    borderRadius: 10,
    fontSize: 14,
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  charCount: {
    fontSize: 12,
    color: '#666',
    textAlign: 'right',
  },
  actions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '8px 16px',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#666',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  preview: {
    padding: 16,
    background: '#fafbff',
    border: '2px solid #e8e9ef',
    borderRadius: 10,
    minHeight: 80,
  },
  currentText: {
    fontSize: 14,
    lineHeight: '1.6',
    color: '#333',
    whiteSpace: 'pre-wrap',
  },
  emptyText: {
    fontSize: 13,
    color: '#999',
    fontStyle: 'italic',
  },
};
